import { useSelector } from "react-redux";
import { type RootStateI } from "../index";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import type Address from "@tootsweet/model/entities/Address";
import { type Dispatch } from "redux";
import type BusinessUser from "@tootsweet/model/entities/BusinessUser";
import OnboardingAPI from "../../app/03_Onboarding/helpers/OnboardingAPI";
import { setJetpackConfig } from "../jetpackConfig";
import { updateCustomer } from "../../app/13_SaaS/api";
import { tt } from "../../utils/i18n/i18n";
import { fetchManagedAccounts } from "../../api/fetchManagedAccounts";
import { setError } from "./app";
import { extractTrackingIDFromURL } from "../../utils/helpers/navigation/extractTrackingIDFromURL";
import { storeLastManagedAccountTrackingID } from "../../utils/helpers/localStorage";

export const SET_MASTER_TOKEN = "SET_MASTER_TOKEN";
export const SET_MANAGED_ACCOUNTS = "SET_MANAGED_ACCOUNTS";
export const ADD_MANAGED_ACCOUNT = "ADD_MANAGED_ACCOUNT";
export const SET_ACCOUNT_TO_DISPLAY = "SET_ACCOUNT_TO_DISPLAY";
export const SET_SHIPPING_ADDRESS = "SET_SHIPPING_ADDRESS";

export const setMasterToken = (masterToken: string | undefined) => {
  return {
    type: SET_MASTER_TOKEN,
    masterToken
  };
};

export const setManagedAccounts = (managedAccounts: ManagedAccount[]) => {
  return {
    type: SET_MANAGED_ACCOUNTS,
    managedAccounts
  };
};

export const addManagedAccount = (managedAccount: ManagedAccount) => {
  return {
    type: ADD_MANAGED_ACCOUNT,
    managedAccount
  };
};

export const setAccountToDisplay = (
  accountToDisplay: ManagedAccount | undefined
) => {
  if (accountToDisplay?.trackingID) {
    storeLastManagedAccountTrackingID(accountToDisplay.trackingID);
  }

  return {
    type: SET_ACCOUNT_TO_DISPLAY,
    accountToDisplay
  };
};

export const useMasterTokenSelector = () => {
  return useSelector<RootStateI, string | undefined>((s) => s.user.masterToken);
};

export const useAccountToDisplaySelector = (): {
  accountToDisplay: ManagedAccount | undefined;
  accountToDisplayIndex: number;
} => {
  const trackingID = extractTrackingIDFromURL();

  const managedAccounts = useSelector<RootStateI, ManagedAccount[]>(
    (s) => s.user.managedAccounts
  );

  if (!trackingID) {
    return {
      accountToDisplay: undefined,
      accountToDisplayIndex: -1
    };
  }

  const accountToDisplayIndex = managedAccounts.findIndex(
    (ma) => ma.trackingID === trackingID
  );

  const accountToDisplay =
    accountToDisplayIndex >= 0
      ? managedAccounts[accountToDisplayIndex]
      : undefined;

  return {
    accountToDisplay,
    accountToDisplayIndex
  };
};

export const useManagedAccountsSelector = () => {
  return useSelector<RootStateI, ManagedAccount[]>(
    (s) => s.user.managedAccounts
  );
};

export const setShippingAddress = (trackingID: string, address: Address) => {
  return {
    type: SET_SHIPPING_ADDRESS,
    trackingID,
    address
  };
};

export const updateCustomerThunk =
  (update: Partial<BusinessUser>, _id?: string, token?: string) =>
  async (dispatch: Dispatch, getState: () => RootStateI) => {
    const state = getState();
    const masterToken = state.user.masterToken;

    if (!masterToken) {
      return;
    }

    if (!_id || !token) {
      return;
    }

    try {
      /**
       * Automatically concatenate the venue's name
       * to the shipping name to avoid delivery issues
       */
      if (
        state.user.accountToDisplay?.placeName &&
        update.shippingAddress?.name &&
        !update.shippingAddress?.name?.includes(
          state.user.accountToDisplay?.placeName
        )
      ) {
        update.shippingAddress.name = `${state.user.accountToDisplay?.placeName} - ${update.shippingAddress.name}`;
      }

      await updateCustomer(update, _id, token);

      if (update.placeName || update.companyName) {
        const config = await OnboardingAPI.updateJetpackDraftConfig(
          _id,
          token,
          { name: update.placeName, companyName: update.companyName || "" }
        );
        dispatch(setJetpackConfig(config));
      }

      const managedAccountsRes = await fetchManagedAccounts(masterToken);

      if (typeof managedAccountsRes === "string") {
        dispatch(setError(managedAccountsRes));
        return;
      }
      const managedAccounts = managedAccountsRes as ManagedAccount[];

      if (managedAccounts?.length) {
        dispatch(setManagedAccounts(managedAccountsRes as ManagedAccount[]));

        if (state.user.accountToDisplay?.trackingID) {
          const atdIdx = managedAccountsRes.findIndex(
            (ma) =>
              !!ma.trackingID &&
              ma.trackingID === state.user.accountToDisplay?.trackingID
          );

          if (atdIdx > -1) {
            const newAtd = managedAccountsRes[atdIdx];
            dispatch(setAccountToDisplay(newAtd));
          }
        }
      }
    } catch (e) {
      console.error(`${e}`);
      alert(tt("customerUpdateError"));
    }
  };
